import { action } from "typesafe-actions";
import { IPartnerMaster, IPartnerMembership, IBulkActivation, IPartnerPackageMaster, IPartnerComplaintLog, IPartnerComplaintReport, IPartnerComplaintByMechanicByCity } from "./Partners.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { closeLoaderWithMessage, openLoaderWithMessage, showMessage } from "../messages/messagesActions";
import { endPoints } from "../../constants/endPoints";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { formatDate2 } from "../../helpers";
import { ISearchQueryParams } from "../common/common.types";
import { addSearchFilterToAdvanceFilter, getSearchQueryV2 } from "../common/helpers";
import axios from "axios";

// ==================== PARTNER MASTER ACTIONS ====================

// Fetch Partner Master List Actions
export const FETCH_PARTNER_MASTER_LIST_PROGRESS = "FETCH_PARTNER_MASTER_LIST_PROGRESS";
export const FETCH_PARTNER_MASTER_LIST_SUCCESS = "FETCH_PARTNER_MASTER_LIST_SUCCESS";
export const FETCH_PARTNER_MASTER_LIST_FAILED = "FETCH_PARTNER_MASTER_LIST_FAILED";

export const fetchPartnerMasterListProgress = () => action(FETCH_PARTNER_MASTER_LIST_PROGRESS);
export const fetchPartnerMasterListSuccess = (list: IPartnerMaster[], totalRecords: number) =>
  action(FETCH_PARTNER_MASTER_LIST_SUCCESS, { list, totalRecords });
export const fetchPartnerMasterListFailed = () => action(FETCH_PARTNER_MASTER_LIST_FAILED);


export const fetchPartnerMasterListAsync =
  (
    queryParams: ISearchQueryParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchPartnerMasterListProgress());

        const {
          page,
          rowsPerPage,
          status,
          date,
          advancedFilters,
          partner_name,
          email,
          partner_prefix
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];


        addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);
        addSearchFilterToAdvanceFilter(advanceFilter, ["email"], email);
        addSearchFilterToAdvanceFilter(advanceFilter, ["partner_prefix"], partner_prefix);


        let finalUrl = `${endPoints.partners.getPartnerMaster}?pageNo=${page}&itemPerPage=${rowsPerPage}`;


        if (date && date.fromDate && date.toDate) {
          finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        if (status && status !== "-1") {
          finalUrl += `&status=${status}`;
        }

        if (advanceFilter.length > 0) {
          finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(finalUrl);
        const data: IPartnerMaster[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchPartnerMasterListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchPartnerMasterListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch partner masters",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Fetch Single Partner Master Actions
export const FETCH_PARTNER_MASTER_PROGRESS = "FETCH_PARTNER_MASTER_PROGRESS";
export const FETCH_PARTNER_MASTER_SUCCESS = "FETCH_PARTNER_MASTER_SUCCESS";
export const FETCH_PARTNER_MASTER_FAILED = "FETCH_PARTNER_MASTER_FAILED";

export const fetchPartnerMasterProgress = () => action(FETCH_PARTNER_MASTER_PROGRESS);
export const fetchPartnerMasterSuccess = (partnerMaster: IPartnerMaster) =>
  action(FETCH_PARTNER_MASTER_SUCCESS, { partnerMaster });
export const fetchPartnerMasterFailed = (errorMessage: string) =>
  action(FETCH_PARTNER_MASTER_FAILED, { errorMessage });

export const fetchPartnerMasterAsync =
  (partnerMasterId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchPartnerMasterProgress());

        const res = await api.get(`${endPoints.partners.getPartnerMaster}?partner_master_uuid=${partnerMasterId}`);
        const data: IPartnerMaster = res.data.data[0];

        dispatch(fetchPartnerMasterSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchPartnerMasterFailed(
            err?.response?.data?.message || "Failed to fetch partner master"
          )
        );
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch partner master",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Upsert Partner Master Actions
export const upsertPartnerMasterAsync =
  (
    partnerMasterData: IPartnerMaster,

    onCallBack: (isSuccess?: boolean, data?: IPartnerMaster) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {

        dispatch(openLoaderWithMessage("Saving partner, please wait..."));
        const {
          create_ts,
          insert_ts,
          created_by_name, created_by_uuid, modified_by_name, modified_by_uuid, user_uuid,
          ...payload
        } = partnerMasterData;

        const res = await api.post(endPoints.partners.upsertPartnerMaster, payload);

        const data: IPartnerMaster = res.data.data;
        dispatch(
          showMessage({
            message: res.data.message,
            type: "success",
            displayAs: "snackbar",
          })
        );
        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to save partner master",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      }
      finally {
        dispatch(closeLoaderWithMessage());
      }
    };

// ==================== PARTNER MEMBERSHIP ACTIONS ====================

// Fetch Partner Membership List Actions
export const FETCH_PARTNER_MEMBERSHIP_LIST_PROGRESS = "FETCH_PARTNER_MEMBERSHIP_LIST_PROGRESS";
export const FETCH_PARTNER_MEMBERSHIP_LIST_SUCCESS = "FETCH_PARTNER_MEMBERSHIP_LIST_SUCCESS";
export const FETCH_PARTNER_MEMBERSHIP_LIST_FAILED = "FETCH_PARTNER_MEMBERSHIP_LIST_FAILED";

export const fetchPartnerMembershipListProgress = () => action(FETCH_PARTNER_MEMBERSHIP_LIST_PROGRESS);
export const fetchPartnerMembershipListSuccess = (list: IPartnerMembership[], totalRecords: number) =>
  action(FETCH_PARTNER_MEMBERSHIP_LIST_SUCCESS, { list, totalRecords });
export const fetchPartnerMembershipListFailed = () => action(FETCH_PARTNER_MEMBERSHIP_LIST_FAILED);


export const fetchPartnerMembershipListAsync =
  (
    queryParams: ISearchQueryParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchPartnerMembershipListProgress());

        const {
          page, rowsPerPage, status, date, advancedFilters, companyOutletUuid,
          contact_number, email_id, vehicle_number, membership_code, package_uuid, partner_id, customer_contact_number
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        // Add email to advance filter if present
        if (email_id) {
          addSearchFilterToAdvanceFilter(advanceFilter, ["email_id"], email_id);
        }

        // Username is already in advancedFilters from the plugin

        let url = `${endPoints.partners.getPartnerMembership}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (companyOutletUuid) url += `&company_outlet_uuid=${companyOutletUuid}`;
        if (status && status !== "-1") url += `&status=${status}`;
        if (date?.fromDate && date?.toDate) url += `&from_date=${date.fromDate}&to_date=${date.toDate}`;

        // Append standard query params
        if (contact_number) url += `&contact_number=${contact_number}`;
        if (vehicle_number) url += `&vehicle_number=${vehicle_number}`;
        if (membership_code) url += `&membership_code=${membership_code}`;
        if (package_uuid) url += `&package_uuid=${package_uuid}`;
        if (partner_id) url += `&partner_id=${partner_id}`;

        if (advanceFilter.length > 0) {
          url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(url);
        const data: IPartnerMembership[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchPartnerMembershipListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchPartnerMembershipListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch partner memberships",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };



// Fetch Single Partner Membership Actions
export const FETCH_PARTNER_MEMBERSHIP_PROGRESS = "FETCH_PARTNER_MEMBERSHIP_PROGRESS";
export const FETCH_PARTNER_MEMBERSHIP_SUCCESS = "FETCH_PARTNER_MEMBERSHIP_SUCCESS";
export const FETCH_PARTNER_MEMBERSHIP_FAILED = "FETCH_PARTNER_MEMBERSHIP_FAILED";

export const fetchPartnerMembershipProgress = () => action(FETCH_PARTNER_MEMBERSHIP_PROGRESS);
export const fetchPartnerMembershipSuccess = (partnerMembership: IPartnerMembership) =>
  action(FETCH_PARTNER_MEMBERSHIP_SUCCESS, { partnerMembership });
export const fetchPartnerMembershipFailed = (errorMessage: string) =>
  action(FETCH_PARTNER_MEMBERSHIP_FAILED, { errorMessage });

export const fetchPartnerMembershipAsync =
  (partnerMembershipId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchPartnerMembershipProgress());

        const res = await api.get(`${endPoints.partners.getPartnerMembership}?partner_membership_uuid=${partnerMembershipId}`);
        const data: IPartnerMembership = res.data.data[0];

        dispatch(fetchPartnerMembershipSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchPartnerMembershipFailed(
            err?.response?.data?.message || "Failed to fetch partner membership"
          )
        );
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch partner membership",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Upsert Partner Membership Actions
export const upsertPartnerMembershipAsync =
  (
    partnerMembershipData: IPartnerMembership,
    isUpdate: boolean,
    onCallBack: (isSuccess?: boolean, data?: IPartnerMembership) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const {
          create_ts,
          insert_ts,
          partner_membership_unique_id,
          modified_by_name, modified_by_uuid, created_by_name, created_by_uuid,
          ...payload
        } = partnerMembershipData;

        const res = await api.post(endPoints.partners.upsertPartnerMembership, payload);

        const data: IPartnerMembership = res.data.data;
        dispatch(
          showMessage({
            message: isUpdate
              ? "Partner membership updated successfully"
              : "Partner membership created successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );
        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to save partner membership",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      }
    };

// Activate Partner Memberships Actions
export const activatePartnerMembershipsAsync =
  (
    payload: {
      partner_id: number;
      company_outlet_uuid: string;
    },
    onCallBack: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        const res = await api.post(endPoints.partners.activateMemberships, payload);

        const data = res.data.data;
        // dispatch(
        //   showMessage({
        //     message: res.data.message || "Partner memberships activated successfully",
        //     type: "success",
        //     displayAs: "snackbar",
        //   })
        // );
        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to activate partner memberships",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      }
    };

// Download Membership Card Actions

/** Fetches membership card as blob for preview. Returns { blob, fileName } or null on error. */
export const fetchMembershipCardForPreviewAsync =
  (
    membershipCode: string,
    companyOutletUuid?: string
  ): ThunkAction<Promise<{ blob: Blob; fileName: string } | null>, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(openLoaderWithMessage("Loading membership card, please wait..."));
        let url = `${endPoints.partners.downloadPartnerMembership}?membership_code=${membershipCode}`;

        if (companyOutletUuid) {
          url += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        const res = await api.get(url, {
          responseType: "blob",
        });

        let fileName = `${membershipCode}_card.png`;
        const contentDisposition = res.headers["content-disposition"];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }

        const blob = new Blob([res.data], { type: "image/png" });
        return { blob, fileName };
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to load membership card",
            type: "error",
            displayAs: "snackbar",
          })
        );
        return null;
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

export const downloadMembershipCardAsync =
  (
    membershipCode: string,
    companyOutletUuid?: string,
    onCallBack?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(openLoaderWithMessage("Downloading membership card, please wait..."));
        let url = `${endPoints.partners.downloadPartnerMembership}?membership_code=${membershipCode}`;

        if (companyOutletUuid) {
          url += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        const res = await api.get(url, {
          responseType: "blob",
        });

        // Extract filename from Content-Disposition header or construct it
        let fileName = `${membershipCode}_card.png`;
        const contentDisposition = res.headers["content-disposition"];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Create a blob from the response
        const blob = new Blob([res.data], { type: "image/png" });

        // Create a temporary URL for the Blob
        const urlObject = window.URL.createObjectURL(blob);

        // Create a link element and simulate a click to trigger the download
        const link = document.createElement("a");
        link.href = urlObject;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        // Clean up by revoking the URL object and removing the link
        window.URL.revokeObjectURL(urlObject);
        document.body.removeChild(link);

        dispatch(
          showMessage({
            message: "Membership card downloaded successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );

        if (onCallBack) {
          onCallBack(true);
        }
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to download membership card",
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallBack) {
          onCallBack(false);
        }
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

// ==================== BULK ACTIVATION ACTIONS ====================

// Fetch Bulk Activation List Actions
export const FETCH_BULK_ACTIVATION_LIST_PROGRESS = "FETCH_BULK_ACTIVATION_LIST_PROGRESS";
export const FETCH_BULK_ACTIVATION_LIST_SUCCESS = "FETCH_BULK_ACTIVATION_LIST_SUCCESS";
export const FETCH_BULK_ACTIVATION_LIST_FAILED = "FETCH_BULK_ACTIVATION_LIST_FAILED";

export const fetchBulkActivationListProgress = () => action(FETCH_BULK_ACTIVATION_LIST_PROGRESS);
export const fetchBulkActivationListSuccess = (list: IBulkActivation[], totalRecords: number) =>
  action(FETCH_BULK_ACTIVATION_LIST_SUCCESS, { list, totalRecords });
export const fetchBulkActivationListFailed = () => action(FETCH_BULK_ACTIVATION_LIST_FAILED);


export const fetchBulkActivationListAsync =
  (
    queryParams: ISearchQueryParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchBulkActivationListProgress());

        let url = getSearchQueryV2(queryParams);

        const {
          partner_id,
          contact_number,
          email_id,
          vehicle_number,
          username,
          // advancedFilters,
        } = queryParams as any;

        // const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];
        // if (username) {
        //   addSearchFilterToAdvanceFilter(advanceFilter, ["username"], username);
        // }

        if (partner_id) {
          url += `&partner_id=${partner_id}`;
        }
        if (username) {
          url += `&username=${username}`;
        }
        if (contact_number) {
          url += `&contact_number=${contact_number}`;
        }
        if (email_id) {
          url += `&email_id=${email_id}`;
        }
        if (vehicle_number) {
          url += `&vehicle_number=${vehicle_number}`;
        }

        const res = await api.get(`${endPoints.partners.getBulkActivation}?${url}`);

        const data: IBulkActivation[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchBulkActivationListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchBulkActivationListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch bulk activations",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };



// Fetch Single Bulk Activation Actions
export const FETCH_BULK_ACTIVATION_PROGRESS = "FETCH_BULK_ACTIVATION_PROGRESS";
export const FETCH_BULK_ACTIVATION_SUCCESS = "FETCH_BULK_ACTIVATION_SUCCESS";
export const FETCH_BULK_ACTIVATION_FAILED = "FETCH_BULK_ACTIVATION_FAILED";

export const fetchBulkActivationProgress = () => action(FETCH_BULK_ACTIVATION_PROGRESS);
export const fetchBulkActivationSuccess = (bulkActivation: IBulkActivation) =>
  action(FETCH_BULK_ACTIVATION_SUCCESS, { bulkActivation });
export const fetchBulkActivationFailed = (errorMessage: string) =>
  action(FETCH_BULK_ACTIVATION_FAILED, { errorMessage });

export const fetchBulkActivationAsync =
  (bulkActivationId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchBulkActivationProgress());

        const res = await api.get(`${endPoints.partners.getBulkActivation}?bulk_activation_uuid=${bulkActivationId}`);
        const data: IBulkActivation = res.data.data[0];

        dispatch(fetchBulkActivationSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchBulkActivationFailed(
            err?.response?.data?.message || "Failed to fetch bulk activation"
          )
        );
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch bulk activation",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Upsert Bulk Activation Actions
export const upsertBulkActivationAsync =
  (
    // bulkActivationData: IBulkActivation,
    formData: FormData,

    onCallBack: (isSuccess?: boolean, data?: IBulkActivation) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        // Send as multipart/form-data
        const res = await api.post(endPoints.partners.upsertBulkActivation, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const data: IBulkActivation = res.data.data;
        dispatch(
          showMessage({
            message: "Bulk activation created successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );
        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to save bulk activation",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      }
    };

// ==================== PARTNER PACKAGE MASTER ACTIONS ====================

// Fetch Partner Package Master List Actions
export const FETCH_PARTNER_PACKAGE_MASTER_LIST_PROGRESS = "FETCH_PARTNER_PACKAGE_MASTER_LIST_PROGRESS";
export const FETCH_PARTNER_PACKAGE_MASTER_LIST_SUCCESS = "FETCH_PARTNER_PACKAGE_MASTER_LIST_SUCCESS";
export const FETCH_PARTNER_PACKAGE_MASTER_LIST_FAILED = "FETCH_PARTNER_PACKAGE_MASTER_LIST_FAILED";

export const fetchPartnerPackageMasterListProgress = () => action(FETCH_PARTNER_PACKAGE_MASTER_LIST_PROGRESS);
export const fetchPartnerPackageMasterListSuccess = (list: IPartnerPackageMaster[], totalRecords: number) =>
  action(FETCH_PARTNER_PACKAGE_MASTER_LIST_SUCCESS, { list, totalRecords });
export const fetchPartnerPackageMasterListFailed = () => action(FETCH_PARTNER_PACKAGE_MASTER_LIST_FAILED);


export const fetchPartnerPackageMasterListAsync =
  (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchPartnerPackageMasterListProgress());

        const {
          page,
          rowsPerPage,
          status,
          date,
          advancedFilters,
          companyOutletUuid,
          partner_name,
          package_name
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);
        addSearchFilterToAdvanceFilter(advanceFilter, ["package_name"], package_name);

        let finalUrl = `${endPoints.partners.getPartnerPackageMaster}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (companyOutletUuid) finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        if (status && status !== "-1") finalUrl += `&status=${status}`;
        if (date?.fromDate && date?.toDate) finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        if (advanceFilter.length > 0) finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;

        const res = await api.get(finalUrl);
        const data: IPartnerPackageMaster[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchPartnerPackageMasterListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchPartnerPackageMasterListFailed());
        dispatch(showMessage({
          message: err?.response?.data?.message || "Failed to fetch packages",
          type: "error",
          displayAs: "snackbar",
        }));
      }
    };

// Fetch Single Partner Package Master Actions
export const FETCH_PARTNER_PACKAGE_MASTER_PROGRESS = "FETCH_PARTNER_PACKAGE_MASTER_PROGRESS";
export const FETCH_PARTNER_PACKAGE_MASTER_SUCCESS = "FETCH_PARTNER_PACKAGE_MASTER_SUCCESS";
export const FETCH_PARTNER_PACKAGE_MASTER_FAILED = "FETCH_PARTNER_PACKAGE_MASTER_FAILED";

export const fetchPartnerPackageMasterProgress = () => action(FETCH_PARTNER_PACKAGE_MASTER_PROGRESS);
export const fetchPartnerPackageMasterSuccess = (partnerPackageMaster: IPartnerPackageMaster) =>
  action(FETCH_PARTNER_PACKAGE_MASTER_SUCCESS, { partnerPackageMaster });
export const fetchPartnerPackageMasterFailed = (errorMessage: string) =>
  action(FETCH_PARTNER_PACKAGE_MASTER_FAILED, { errorMessage });

export const fetchPartnerPackageMasterAsync =
  (partnerPackageMasterId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchPartnerPackageMasterProgress());

        const res = await api.get(`${endPoints.partners.getPartnerPackageMaster}?partner_package_master_uuid=${partnerPackageMasterId}`);
        const data: IPartnerPackageMaster = res.data.data[0];

        dispatch(fetchPartnerPackageMasterSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchPartnerPackageMasterFailed(
            err?.response?.data?.message || "Failed to fetch partner package master"
          )
        );
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch partner package master",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Upsert Partner Package Master Actions
export const upsertPartnerPackageMasterAsync =
  (
    partnerPackageMasterData: IPartnerPackageMaster,
    onCallBack: (isSuccess?: boolean, data?: IPartnerPackageMaster) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(openLoaderWithMessage("Saving partner package, please wait..."));
        const {
          create_ts,
          insert_ts,
          created_by_name,
          created_by_uuid,
          modified_by_name,
          modified_by_uuid,
          ...payload
        } = partnerPackageMasterData;

        const res = await api.post(endPoints.partners.upsertPartnerPackageMaster, payload);

        const data: IPartnerPackageMaster = res.data.data;
        dispatch(
          showMessage({
            message: res.data.message || "Partner package master saved successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );
        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to save partner package master",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

// ==================== COMPLAINT LOG ACTIONS ====================

// Fetch Complaint Log List Actions
export const FETCH_COMPLAINT_LOG_LIST_PROGRESS = "FETCH_COMPLAINT_LOG_LIST_PROGRESS";
export const FETCH_COMPLAINT_LOG_LIST_SUCCESS = "FETCH_COMPLAINT_LOG_LIST_SUCCESS";
export const FETCH_COMPLAINT_LOG_LIST_FAILED = "FETCH_COMPLAINT_LOG_LIST_FAILED";

export const fetchComplaintLogListProgress = () => action(FETCH_COMPLAINT_LOG_LIST_PROGRESS);
export const fetchComplaintLogListSuccess = (list: IPartnerComplaintLog[], totalRecords: number) =>
  action(FETCH_COMPLAINT_LOG_LIST_SUCCESS, { list, totalRecords });
export const fetchComplaintLogListFailed = () => action(FETCH_COMPLAINT_LOG_LIST_FAILED);


export const fetchComplaintLogListAsync =
  (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchComplaintLogListProgress());

        const {
          page,
          rowsPerPage,
          status,
          date,
          advancedFilters,
          companyOutletUuid,
          partner_name,
          partner_id,
          complaint_no,
          vehicle_number,
          membership_code,
          contact_number,
          reg_contact_no
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);
        if (partner_id) {
          addSearchFilterToAdvanceFilter(advanceFilter, ["partner_id"], partner_id);
        }
        addSearchFilterToAdvanceFilter(advanceFilter, ["complaint_no"], complaint_no);
        addSearchFilterToAdvanceFilter(advanceFilter, ["vehicle_number"], vehicle_number);
        addSearchFilterToAdvanceFilter(advanceFilter, ["membership_code"], membership_code);
        addSearchFilterToAdvanceFilter(advanceFilter, ["reg_contact_no"], reg_contact_no || contact_number);

        let finalUrl = `${endPoints.partners.getComplaintLog}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (companyOutletUuid) {
          finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        if (status && status !== "-1") {
          finalUrl += `&status=${status}`;
        }

        if (date?.fromDate && date?.toDate) {
          finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        if (advanceFilter.length > 0) {
          finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(finalUrl);
        const data: IPartnerComplaintLog[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchComplaintLogListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchComplaintLogListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch complaint logs",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };
// ==================== COMPLAINTS BY MECHANIC AND BY CITY REPORT ACTIONS ====================
export const FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_PROGRESS = "FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_PROGRESS"
export const FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_SUCCESS = "FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_SUCCESS"
export const FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_FAILED = "FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_FAILED"

export const fetchComplaintByMechanicByCityListProgress = () => action(FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_PROGRESS);
export const fetchComplaintByMechanicByCityListSuccess = (list: IPartnerComplaintByMechanicByCity[], totalRecords: number) =>
  action(FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_SUCCESS, { list, totalRecords });
export const fetchComplaintByMechanicByCityListFailed = () => action(FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_FAILED);

export const fetchComplaintByMechanicByCity =
  (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchComplaintByMechanicByCityListProgress());

        const {
          page,
          rowsPerPage,
          status,
          date,
          advancedFilters,
          companyOutletUuid,
          assigned_mechanic_id,
          mechanic_id,
          service_city,
          total_complaints,
          open_complaints,
          closed_complaints,
          partner_name,
          partner_id,
          type
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        let finalUrl = `${endPoints.partners.getComplaintReport}?type=${type}&pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (companyOutletUuid) {
          finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        if (partner_id) {
          finalUrl += `&partner_id=${partner_id}`;
        }

        if (status && status !== "-1") {
          finalUrl += `&status=${status}`;
        }

        if (date?.fromDate && date?.toDate) {
          finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        if (partner_name) {
          finalUrl += `&partner_name=${partner_name}`;
        }

        if (mechanic_id) {
          finalUrl += `&mechanic_id=${mechanic_id}`;
        }

        if (service_city) {
          finalUrl += `&city=${service_city}`;
        }

        if (advanceFilter.length > 0) {
          finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(finalUrl);
        const data: IPartnerComplaintByMechanicByCity[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchComplaintByMechanicByCityListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchComplaintByMechanicByCityListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch complaint logs",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// ==================== COMPLAINT REPORT ACTIONS ====================

export const FETCH_COMPLAINT_REPORT_LIST_PROGRESS = "FETCH_COMPLAINT_REPORT_LIST_PROGRESS";
export const FETCH_COMPLAINT_REPORT_LIST_SUCCESS = "FETCH_COMPLAINT_REPORT_LIST_SUCCESS";
export const FETCH_COMPLAINT_REPORT_LIST_FAILED = "FETCH_COMPLAINT_REPORT_LIST_FAILED";

export const fetchComplaintReportListProgress = () => action(FETCH_COMPLAINT_REPORT_LIST_PROGRESS);
export const fetchComplaintReportListSuccess = (list: IPartnerComplaintReport[], totalRecords: number) =>
  action(FETCH_COMPLAINT_REPORT_LIST_SUCCESS, { list, totalRecords });
export const fetchComplaintReportListFailed = () => action(FETCH_COMPLAINT_REPORT_LIST_FAILED);

export const fetchComplaintReportListAsync =
  (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchComplaintReportListProgress());

        const {
          page,
          rowsPerPage,
          status,
          date,
          advancedFilters,
          companyOutletUuid,
          complaint_no,
          vehicle_number,
          membership_code,
          mechanic_id,
          mechanic_name,
          service_city,
          partner_name,
          partner_id,
          reg_contact_no,
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        addSearchFilterToAdvanceFilter(advanceFilter, ["complaint_no"], complaint_no);
        addSearchFilterToAdvanceFilter(advanceFilter, ["vehicle_number"], vehicle_number);
        addSearchFilterToAdvanceFilter(advanceFilter, ["membership_code"], membership_code);
        addSearchFilterToAdvanceFilter(advanceFilter, ["reg_contact_no"], reg_contact_no);

        if (service_city) {
          addSearchFilterToAdvanceFilter(advanceFilter, ["service_city"], service_city);
        }
        if (partner_name) {
          addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);
        }

        if (mechanic_name) {
          addSearchFilterToAdvanceFilter(advanceFilter, ["mechanic_name"], mechanic_name);
        }

        if (mechanic_id) {
          addSearchFilterToAdvanceFilter(advanceFilter, ["assigned_mechanic_id"], mechanic_id);
        }

        let finalUrl = `${endPoints.crossroadsLeads.getCrossroadsComplaintLog}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (companyOutletUuid) {
          finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        if (partner_id) {
          finalUrl += `&partner_id=${partner_id}`;
        }

        // if(mechanic_id){
        //   finalUrl += `&mechanic_id=${mechanic_id}`;
        // }

        if (status && status !== "-1") {
          finalUrl += `&status=${status}`;
        }

        if (date?.fromDate && date?.toDate) {
          finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        if (advanceFilter.length > 0) {
          finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(finalUrl);
        const data: IPartnerComplaintLog[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchComplaintReportListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchComplaintReportListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch crossroads complaint logs",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Clear Actions
export const CLEAR_PARTNER_MASTER_STATE = "CLEAR_PARTNER_MASTER_STATE";
export const CLEAR_PARTNER_MASTER = "CLEAR_PARTNER_MASTER";
export const CLEAR_PARTNER_MEMBERSHIP_STATE = "CLEAR_PARTNER_MEMBERSHIP_STATE";
export const CLEAR_PARTNER_MEMBERSHIP = "CLEAR_PARTNER_MEMBERSHIP";
export const CLEAR_BULK_ACTIVATION_STATE = "CLEAR_BULK_ACTIVATION_STATE";
export const CLEAR_BULK_ACTIVATION = "CLEAR_BULK_ACTIVATION";
export const CLEAR_PARTNER_PACKAGE_MASTER_STATE = "CLEAR_PARTNER_PACKAGE_MASTER_STATE";
export const CLEAR_PARTNER_PACKAGE_MASTER = "CLEAR_PARTNER_PACKAGE_MASTER";
export const CLEAR_COMPLAINT_LOG_STATE = "CLEAR_COMPLAINT_LOG_STATE";
export const CLEAR_COMPLAINT_BY_MECHANIC_BY_CITY_STATE = "CLEAR_COMPLAINT_BY_MECHANIC_BY_CITY_STATE";
export const CLEAR_COMPLAINT_REPORT_DETAILS_LIST_STATE = "CLEAR_COMPLAINT_REPORT_DETAILS_LIST_STATE";

export const clearPartnerMasterState = () => action(CLEAR_PARTNER_MASTER_STATE);
export const clearPartnerMaster = () => action(CLEAR_PARTNER_MASTER);
export const clearPartnerMembershipState = () => action(CLEAR_PARTNER_MEMBERSHIP_STATE);
export const clearPartnerMembership = () => action(CLEAR_PARTNER_MEMBERSHIP);
export const clearBulkActivationState = () => action(CLEAR_BULK_ACTIVATION_STATE);
export const clearBulkActivation = () => action(CLEAR_BULK_ACTIVATION);
export const clearPartnerPackageMasterState = () => action(CLEAR_PARTNER_PACKAGE_MASTER_STATE);
export const clearPartnerPackageMaster = () => action(CLEAR_PARTNER_PACKAGE_MASTER);
export const clearComplaintLogState = () => action(CLEAR_COMPLAINT_LOG_STATE);
export const clearComplaintByMechanicByCity = () => action(CLEAR_COMPLAINT_BY_MECHANIC_BY_CITY_STATE)
export const clearComplaintReportDetailsListState = () => action(CLEAR_COMPLAINT_REPORT_DETAILS_LIST_STATE);

// ==================== CHECK VEHICLE PHONE EXIST ACTIONS ====================
export const CHECK_VEHICLE_PHONE_EXIST_PROGRESS = "CHECK_VEHICLE_PHONE_EXIST_PROGRESS";
export const CHECK_VEHICLE_PHONE_EXIST_SUCCESS = "CHECK_VEHICLE_PHONE_EXIST_SUCCESS";
export const CHECK_VEHICLE_PHONE_EXIST_FAILED = "CHECK_VEHICLE_PHONE_EXIST_FAILED";

export const checkVehiclePhoneExistProgress = () => action(CHECK_VEHICLE_PHONE_EXIST_PROGRESS);
export const checkVehiclePhoneExistSuccess = (data: any) => action(CHECK_VEHICLE_PHONE_EXIST_SUCCESS, { data });
export const checkVehiclePhoneExistFailed = (errorMessage: string) => action(CHECK_VEHICLE_PHONE_EXIST_FAILED, { errorMessage });

export const checkVehiclePhoneExistAsync = (
  searchType: string,
  value: string,
  token: string,
  onCallback?: (isSuccess: boolean, data?: any) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(checkVehiclePhoneExistProgress());

      // ✅ Just the path — api instance adds baseURL automatically
      const baseUrl =
        searchType === "Mobile Number"
          ? (process.env.REACT_APP_SERVER2_URL || process.env.REACT_APP_COMPLAINT_SEARCH_URL || process.env.REACT_APP_SERVER_URL)
          : (process.env.REACT_APP_COMPLAINT_SEARCH_URL || process.env.REACT_APP_SERVER_URL);

      const url =
        searchType === "Mobile Number"
          ? `${baseUrl}/check-vehicle-phone-exist?phone_number=${value}`
          : searchType === "Vehicle Number"
            ? `${baseUrl}/check-vehicle-phone-exist?vehicle_number=${value}`
            : `${baseUrl}/check-vehicle-phone-exist?certificate_number=${value}`;

      const res = await api.get(url, {
        headers: {
          "auth-Token": token,
        },
      });

      const data = res.data?.data || [];
      dispatch(checkVehiclePhoneExistSuccess(data));
      onCallback?.(true, data);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || "Failed to check vehicle/phone existence";
      dispatch(checkVehiclePhoneExistFailed(errorMsg));
      onCallback?.(false, null);
    }
  };